import React, { createContext, useContext, useState, useEffect } from "react"
import { useMutation, useQuery } from "urql"

import { useToast } from "../../contexts/ToastContext"
import { urqlClient } from "../../utils/utils"

import { generateColorPalette } from "./utils/enhancedColorUtils"

// GraphQL queries and mutations for Builder v2
const GET_PAGES_QUERY = `
  query GetPages {
    builderPages {
      id
      slug
      title
      sectionsCount
      isCustom
      createdAt
      updatedAt
    }
  }
`

const GET_PAGE_QUERY = `
  query GetPage($slug: String!) {
    builderPage(slug: $slug) {
      id
      slug
      title
      sectionsCount
      isCustom
      createdAt
      updatedAt
    }
    builderPageSections(pageSlug: $slug) {
      id
      layout
      order
      headline
      subheadline
      imageUrl
      body
      settings

      hidden
      createdAt
      updatedAt
    }
  }
`

const GET_THEME_QUERY = `
  query GetTheme {
    builderTheme {
      id
      colorPrimary
      colorPrimaryDark
      colorSecondary
      colorSecondaryDark
      colorAccent
      colorTextLight
      colorTextDark
      colorBgLight
      colorBgDark
      colorBgMedium
      colorBgExtraLight
      colorWhite
      colorGreyLightest
      colorGreyLight
      colorGrey
      colorGreyDark
      colorGreyDarkest
      colorBlack
      colorReviewStars
      fontHeading
      fontParagraph
      fontHeadingWeight
      fontParagraphRegularWeight
      fontParagraphBoldWeight
      buttonStyle
      borderRadius
      shadowStyle
      spacingStyle
      builderThemeId
      createdAt
      updatedAt
      # Computed fields for frontend compatibility
      fonts
      colors
      header
      footer
    }
    builderPresetThemes {
      id
      name
      colorPrimary
      colorPrimaryDark
      colorSecondary
      colorSecondaryDark
      colorAccent
      colorTextLight
      colorTextDark
      colorBgLight
      colorBgDark
      colorWhite
      colorGreyLightest
      colorGreyLight
      colorGrey
      colorGreyDark
      colorGreyDarkest
      colorBlack
      colorReviewStars
      fontHeading
      fontParagraph
      fontHeadingWeight
      fontParagraphRegularWeight
      fontParagraphBoldWeight
      buttonStyle
      borderRadius
      shadowStyle
      spacingStyle
    }
  }
`

const GET_AVAILABLE_LAYOUTS_QUERY = `
  query GetAvailableLayouts {
    builderAvailableLayouts
  }
`

const CREATE_PAGE_MUTATION = `
  mutation CreatePage($title: String!, $slug: String) {
    createBuilderPage(title: $title, slug: $slug) {
      page {
        id
        slug
        title
        sectionsCount
        createdAt
        updatedAt
      }
      errors
    }
  }
`

const UPDATE_PAGE_MUTATION = `
  mutation UpdatePage($slug: String!, $title: String!, $newSlug: String) {
    updateBuilderPage(slug: $slug, title: $title, newSlug: $newSlug) {
      page {
        id
        slug
        title
        sectionsCount
        createdAt
        updatedAt
      }
      errors
    }
  }
`

const DELETE_PAGE_MUTATION = `
  mutation DeletePage($slug: String!) {
    deleteBuilderPage(slug: $slug) {
      success
      errors
    }
  }
`

const CREATE_SECTION_MUTATION = `
  mutation CreateSection($pageSlug: String!, $layout: String!, $headline: String, $subheadline: String, $body: String, $imageUrl: String, $order: Int, $settings: JSON) {

    createBuilderSection(pageSlug: $pageSlug, layout: $layout, headline: $headline, subheadline: $subheadline, body: $body, imageUrl: $imageUrl, order: $order, settings: $settings) {
      section {
        id
        layout
        order
        headline
        subheadline
        imageUrl
        body
        settings
        hidden
        createdAt
        updatedAt
      }
      errors
    }
  }
`

const UPDATE_SECTION_MUTATION = `
  mutation UpdateSection($pageSlug: String!, $sectionId: ID!, $layout: String, $headline: String, $subheadline: String, $body: String, $imageUrl: String, $order: Int, $settings: JSON, $hidden: Boolean) {
    updateBuilderSection(pageSlug: $pageSlug, sectionId: $sectionId, layout: $layout, headline: $headline, subheadline: $subheadline, body: $body, imageUrl: $imageUrl, order: $order, settings: $settings, hidden: $hidden) {
      section {
        id
        layout
        order
        headline
        subheadline
        imageUrl
        body
        settings
        hidden
        createdAt
        updatedAt
      }
      errors
    }
  }
`

const DELETE_SECTION_MUTATION = `
  mutation DeleteSection($pageSlug: String!, $sectionId: ID!) {
    deleteBuilderSection(pageSlug: $pageSlug, sectionId: $sectionId) {
      success
      errors
    }
  }
`

const MOVE_SECTION_MUTATION = `
  mutation MoveSection($pageSlug: String!, $sectionId: ID!, $direction: String!) {
    moveBuilderSection(pageSlug: $pageSlug, sectionId: $sectionId, direction: $direction) {
      success
      errors
    }
  }
`

const UPDATE_THEME_MUTATION = `
  mutation UpdateTheme($colors: JSON, $fonts: JSON, $buttonStyle: String, $borderRadius: String, $shadowStyle: String, $spacingStyle: String, $header: JSON, $footer: JSON) {
    updateBuilderTheme(colors: $colors, fonts: $fonts, buttonStyle: $buttonStyle, borderRadius: $borderRadius, shadowStyle: $shadowStyle, spacingStyle: $spacingStyle, header: $header, footer: $footer) {
      theme {
        id
        colorPrimary
        colorPrimaryDark
        colorSecondary
        colorSecondaryDark
        colorAccent
        colorTextLight
        colorTextDark
        colorBgLight
        colorBgDark
        colorWhite
        colorGreyLightest
        colorGreyLight
        colorGrey
        colorGreyDark
        colorGreyDarkest
        colorBlack
        colorReviewStars
        fontHeading
        fontParagraph
        fontHeadingWeight
        fontParagraphRegularWeight
        fontParagraphBoldWeight
        buttonStyle
        borderRadius
        shadowStyle
        spacingStyle
        builderThemeId
        createdAt
        updatedAt
        # Computed fields for frontend compatibility
        fonts
        colors
        header
        footer
      }
      errors
    }
  }
`

const APPLY_PRESET_THEME_MUTATION = `
  mutation ApplyPresetTheme($builderThemeId: ID!) {
    applyBuilderPresetTheme(builderThemeId: $builderThemeId) {
      theme {
        id
        colorPrimary
        colorPrimaryDark
        colorSecondary
        colorSecondaryDark
        colorAccent
        colorTextLight
        colorTextDark
        colorBgLight
        colorBgDark
        colorWhite
        colorGreyLightest
        colorGreyLight
        colorGrey
        colorGreyDark
        colorGreyDarkest
        colorBlack
        colorReviewStars
        fontHeading
        fontParagraph
        fontHeadingWeight
        fontParagraphRegularWeight
        fontParagraphBoldWeight
        buttonStyle
        borderRadius
        shadowStyle
        spacingStyle
        builderThemeId
        createdAt
        updatedAt
        # Computed fields for frontend compatibility
        fonts
        colors
        header
        footer
      }
      errors
    }
  }
`

const WebsiteBuilderV2Context = createContext()

export const WebsiteBuilderV2Provider = ({ practice: practiceProp, currentPageSlug, children }) => {
  const { showToast } = useToast()

  // State
  const [practice, setPractice] = useState(practiceProp)
  const [pages, setPages] = useState([])
  const [currentPage, setCurrentPage] = useState(null)
  const [sections, setSections] = useState([])
  const [selectedPageSlug, setSelectedPageSlug] = useState(currentPageSlug)
  const [theme, setTheme] = useState(null)
  const [previewTheme, setPreviewTheme] = useState(null) // Separate state for temporary preview
  const [previewSections, setPreviewSections] = useState(null) // Separate state for temporary section preview
  const [presetThemes, setPresetThemes] = useState([])
  const [availableLayouts, setAvailableLayouts] = useState([])
  const [selectedSection, setSelectedSection] = useState(null)
  const [highlightedField, setHighlightedField] = useState(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [openField, setOpenField] = useState(null)
  const [activeAccordionSection, setActiveAccordionSection] = useState(null)
  const [expandedPages, setExpandedPages] = useState(new Set())
  const [showAddSectionScreen, setShowAddSectionScreen] = useState(false)
  const [showEditSectionScreen, setShowEditSectionScreen] = useState(false)
  const [editingSection, setEditingSection] = useState(null)

  // Loading states
  const [isLoading, setIsLoading] = useState(false)

  // GraphQL hooks
  const [{ data: pagesData, fetching: pagesLoading }, refetchPages] = useQuery({
    query: GET_PAGES_QUERY
  })

  const [{ data: pageData, fetching: pageLoading }] = useQuery({
    query: GET_PAGE_QUERY,
    variables: { slug: selectedPageSlug },
    pause: !selectedPageSlug
  })

  const [{ data: themeData, fetching: themeLoading }] = useQuery({
    query: GET_THEME_QUERY
  })

  const [{ data: layoutsData, fetching: layoutsLoading }] = useQuery({
    query: GET_AVAILABLE_LAYOUTS_QUERY
  })

  const [{ fetching: createPageLoading }, createPage] = useMutation(CREATE_PAGE_MUTATION)
  const [{ fetching: updatePageLoading }, updatePage] = useMutation(UPDATE_PAGE_MUTATION)
  const [{ fetching: deletePageLoading }, deletePage] = useMutation(DELETE_PAGE_MUTATION)
  const [{ fetching: createSectionLoading }, createSection] = useMutation(CREATE_SECTION_MUTATION)
  const [{ fetching: updateSectionLoading }, updateSection] = useMutation(UPDATE_SECTION_MUTATION)
  const [{ fetching: deleteSectionLoading }, deleteSection] = useMutation(DELETE_SECTION_MUTATION)
  const [{ fetching: moveSectionLoading }, moveSection] = useMutation(MOVE_SECTION_MUTATION)
  const [{ fetching: updateThemeLoading }, updateTheme] = useMutation(UPDATE_THEME_MUTATION)
  const [{ fetching: applyPresetThemeLoading }, applyPresetTheme] = useMutation(APPLY_PRESET_THEME_MUTATION)

  const fetching =
    pagesLoading ||
    pageLoading ||
    themeLoading ||
    layoutsLoading ||
    createPageLoading ||
    updatePageLoading ||
    deletePageLoading ||
    createSectionLoading ||
    updateSectionLoading ||
    deleteSectionLoading ||
    moveSectionLoading ||
    updateThemeLoading ||
    applyPresetThemeLoading

  // Load initial data - now handled by automatic GraphQL queries

  useEffect(() => {
    if (selectedPageSlug) {
      // The GraphQL query will automatically execute when selectedPageSlug changes
    }
  }, [selectedPageSlug])

  // Handle pages data when loaded
  useEffect(() => {
    if (pagesData?.builderPages) {
      setPages(pagesData.builderPages)

      // Auto-select the first page if no page is currently selected
      if (pagesData.builderPages.length > 0 && !selectedPageSlug) {
        selectPage(pagesData.builderPages[0])
      }
    }
  }, [pagesData, selectedPageSlug])

  // Handle page data when loaded
  useEffect(() => {
    if (pageData?.builderPage) {
      setCurrentPage(pageData.builderPage)
      setSections(pageData.builderPageSections || [])
    } else if (pageData?.builderPageSections) {
      // Handle case where only sections data is available
      setSections(pageData.builderPageSections)
    }
  }, [pageData])

  // Helper function to transform theme data for component compatibility
  const transformThemeData = (theme) => {
    if (!theme) return null

    return {
      ...theme,
      fonts: {
        heading: theme.fontHeading,
        paragraph: theme.fontParagraph,
        headingWeight: theme.fontHeadingWeight,
        paragraphRegularWeight: theme.fontParagraphRegularWeight,
        paragraphBoldWeight: theme.fontParagraphBoldWeight
      },
      colors: theme.colors || {
        // Fallback to individual fields if computed colors field is not available
        primary: theme.colorPrimary,
        primaryDark: theme.colorPrimaryDark,
        secondary: theme.colorSecondary,
        secondaryDark: theme.colorSecondaryDark,
        accent: theme.colorAccent,
        textLight: theme.colorTextLight,
        textDark: theme.colorTextDark,
        bgLight: theme.colorBgLight,
        bgDark: theme.colorBgDark,
        bgMedium: theme.colorBgMedium,
        bgExtraLight: theme.colorBgExtraLight,
        white: theme.colorWhite,
        greyLightest: theme.colorGreyLightest,
        greyLight: theme.colorGreyLight,
        grey: theme.colorGrey,
        greyDark: theme.colorGreyDark,
        greyDarkest: theme.colorGreyDarkest,
        black: theme.colorBlack,
        reviewStars: theme.colorReviewStars
      },
      header: theme.header || {},
      footer: theme.footer || {}
    }
  }

  // Handle theme data when loaded
  useEffect(() => {
    if (themeData?.builderTheme) {
      setTheme(transformThemeData(themeData.builderTheme))
    }
    if (themeData?.builderPresetThemes) {
      setPresetThemes(themeData.builderPresetThemes)
    }
  }, [themeData])

  // Handle layouts data when loaded
  useEffect(() => {
    if (layoutsData?.builderAvailableLayouts) {
      // Map UX section types to user-friendly labels
      const sectionTypeLabels = {
        hero: "Hero",
        sub_hero: "Hero",
        services: "Services",
        featured_services: "Featured Services",
        gallery: "Gallery",
        reviews: "Reviews",
        content: "Content",
        quote: "Quote",
        cta_content: "CTA Content",
        cta_banner: "CTA Banner",
        faq: "FAQ",
        keywords: "Keywords",
        packages: "Packages",
        events: "Events",
        credentials: "Credentials",
        contact: "Contact"
      }

      setAvailableLayouts(
        layoutsData.builderAvailableLayouts.map((sectionType) => ({
          value: sectionType,
          label:
            sectionTypeLabels[sectionType] ||
            sectionType.charAt(0).toUpperCase() + sectionType.slice(1).replace(/_/g, " ")
        }))
      )
    }
  }, [layoutsData])

  // Data loading functions

  const selectPage = async (page) => {
    setSelectedPageSlug(page.slug)
    setCurrentPage(page)
    // The sections will be loaded automatically by the GraphQL query when selectedPageSlug changes
  }

  // Page management functions
  const handleCreatePage = async (title, slug = null) => {
    try {
      setIsLoading(true)
      const result = await createPage({ title, slug })
      if (result.data?.createBuilderPage?.page) {
        // Immediately add the new page to local state
        setPages((prevPages) => [...prevPages, result.data.createBuilderPage.page])

        // Select the newly created page
        selectPage(result.data.createBuilderPage.page)

        // Refetch pages to ensure we have the latest data from the server
        await refetchPages()

        showToast("Page created successfully", "success")
        return result.data.createBuilderPage.page
      } else if (result.data?.createBuilderPage?.errors?.length > 0) {
        showToast(result.data.createBuilderPage.errors[0], "error")
        throw new Error(result.data.createBuilderPage.errors[0])
      }
    } catch (error) {
      showToast("Failed to create page", "error")
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdatePage = async (slug, title, newSlug = null) => {
    try {
      setIsLoading(true)
      const result = await updatePage({ slug, title, newSlug })
      if (result.data?.updateBuilderPage?.page) {
        // Immediately update the page in local state
        setPages((prevPages) =>
          prevPages.map((page) => (page.slug === slug ? result.data.updateBuilderPage.page : page))
        )

        // Update current page if it's the one being updated
        if (currentPage?.slug === slug) {
          setCurrentPage(result.data.updateBuilderPage.page)
        }

        // Refetch pages to ensure we have the latest data from the server
        await refetchPages()

        showToast("Page updated successfully", "success")
        return result.data.updateBuilderPage.page
      } else if (result.data?.updateBuilderPage?.errors?.length > 0) {
        showToast(result.data.updateBuilderPage.errors[0], "error")
        throw new Error(result.data.updateBuilderPage.errors[0])
      }
    } catch (error) {
      showToast("Failed to update page", "error")
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeletePage = async (slug) => {
    try {
      setIsLoading(true)
      const result = await deletePage({ slug })
      if (result.data?.deleteBuilderPage?.success) {
        // Clear current page if it was the deleted one
        if (currentPage?.slug === slug) {
          setCurrentPage(null)
          setSections([])
          setSelectedPageSlug(null)
        }

        // Immediately update the local state to remove the deleted page
        setPages((prevPages) => prevPages.filter((page) => page.slug !== slug))

        // Wait a moment for the server to process the deletion
        await new Promise((resolve) => setTimeout(resolve, 200))

        // Force a fresh network request to invalidate the cache
        // Use a more aggressive cache invalidation approach
        try {
          await urqlClient
            .query(GET_PAGES_QUERY, undefined, {
              requestPolicy: "network-only",
              fetchOptions: {
                headers: {
                  ...urqlClient.fetchOptions.headers,
                  "Cache-Control": "no-cache"
                }
              }
            })
            .toPromise()
        } catch (cacheError) {
          console.warn("Cache invalidation failed:", cacheError) // eslint-disable-line no-console
        }

        showToast("Page deleted successfully", "success")
      } else if (result.data?.deleteBuilderPage?.errors?.length > 0) {
        showToast(result.data.deleteBuilderPage.errors[0], "error")
        throw new Error(result.data.deleteBuilderPage.errors[0])
      }
    } catch (error) {
      showToast(`Failed to delete page: ${error.message}`, "error")
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Section management functions
  const handleCreateSection = async (layout, data = {}) => {
    if (!currentPage) return

    try {
      setIsLoading(true)
      const result = await createSection({
        pageSlug: currentPage.slug,
        layout,
        ...data
      })
      if (result.data?.createBuilderSection?.section) {
        setSections([...sections, result.data.createBuilderSection.section])

        // Update the current page's sectionsCount in the pages list
        setPages((prevPages) =>
          prevPages.map((page) =>
            page.slug === currentPage.slug ? { ...page, sectionsCount: page.sectionsCount + 1 } : page
          )
        )

        // Also update the current page state
        setCurrentPage((prevPage) => ({
          ...prevPage,
          sectionsCount: prevPage.sectionsCount + 1
        }))

        // Force a fresh network request to invalidate the cache
        try {
          await urqlClient
            .query(GET_PAGES_QUERY, undefined, {
              requestPolicy: "network-only",
              fetchOptions: {
                headers: {
                  ...urqlClient.fetchOptions.headers,
                  "Cache-Control": "no-cache"
                }
              }
            })
            .toPromise()
        } catch (cacheError) {
          console.warn("Cache invalidation failed:", cacheError) // eslint-disable-line no-console
        }

        showToast("Section created successfully", "success")
        return result.data.createBuilderSection.section
      } else if (result.data?.createBuilderSection?.errors?.length > 0) {
        showToast(result.data.createBuilderSection.errors[0], "error")
        throw new Error(result.data.createBuilderSection.errors[0])
      }
    } catch (error) {
      showToast("Failed to create section", "error")
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Handle creating multiple sections at once
  const handleCreateMultipleSections = async (sectionTypes) => {
    if (!currentPage || sectionTypes.length === 0) return

    try {
      setIsLoading(true)
      const newSections = []

      for (const sectionType of sectionTypes) {
        const result = await createSection({
          pageSlug: currentPage.slug,
          layout: sectionType.value
        })

        if (result.data?.createBuilderSection?.section) {
          newSections.push(result.data.createBuilderSection.section)
        } else if (result.data?.createBuilderSection?.errors?.length > 0) {
          showToast(`Failed to create ${sectionType.label}: ${result.data.createBuilderSection.errors[0]}`, "error")
        }
      }

      if (newSections.length > 0) {
        setSections([...sections, ...newSections])

        // Update the current page's sectionsCount in the pages list
        setPages((prevPages) =>
          prevPages.map((page) =>
            page.slug === currentPage.slug ? { ...page, sectionsCount: page.sectionsCount + newSections.length } : page
          )
        )

        // Also update the current page state
        setCurrentPage((prevPage) => ({
          ...prevPage,
          sectionsCount: prevPage.sectionsCount + newSections.length
        }))

        // Force a fresh network request to invalidate the cache
        try {
          await urqlClient
            .query(GET_PAGES_QUERY, undefined, {
              requestPolicy: "network-only",
              fetchOptions: {
                headers: {
                  ...urqlClient.fetchOptions.headers,
                  "Cache-Control": "no-cache"
                }
              }
            })
            .toPromise()
        } catch (cacheError) {
          console.warn("Cache invalidation failed:", cacheError) // eslint-disable-line no-console
        }

        showToast(`${newSections.length} section${newSections.length > 1 ? "s" : ""} created successfully`, "success")
      }

      return newSections
    } catch (error) {
      showToast("Failed to create sections", "error")
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateSection = async (sectionId, data) => {
    if (!currentPage) return

    try {
      setIsLoading(true)
      const result = await updateSection({
        pageSlug: currentPage.slug,
        sectionId,
        ...data
      })
      if (result.data?.updateBuilderSection?.section) {
        setSections(sections.map((s) => (s.id === sectionId ? result.data.updateBuilderSection.section : s)))
        setPreviewSections(null) // Clear preview after successful update
        showToast("Section updated successfully", "success")
        return result.data.updateBuilderSection.section
      } else if (result.data?.updateBuilderSection?.errors?.length > 0) {
        showToast(result.data.updateBuilderSection.errors[0], "error")
        throw new Error(result.data.updateBuilderSection.errors[0])
      }
    } catch (error) {
      showToast("Failed to update section", "error")
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteSection = async (sectionId) => {
    if (!currentPage) return

    try {
      setIsLoading(true)
      const result = await deleteSection({
        pageSlug: currentPage.slug,
        sectionId
      })
      if (result.data?.deleteBuilderSection?.success) {
        setSections(sections.filter((s) => s.id !== sectionId))

        // Update the current page's sectionsCount in the pages list
        setPages((prevPages) =>
          prevPages.map((page) =>
            page.slug === currentPage.slug ? { ...page, sectionsCount: Math.max(0, page.sectionsCount - 1) } : page
          )
        )

        // Also update the current page state
        setCurrentPage((prevPage) => ({
          ...prevPage,
          sectionsCount: Math.max(0, prevPage.sectionsCount - 1)
        }))

        // Force a fresh network request to invalidate the cache
        try {
          await urqlClient
            .query(GET_PAGES_QUERY, undefined, {
              requestPolicy: "network-only",
              fetchOptions: {
                headers: {
                  ...urqlClient.fetchOptions.headers,
                  "Cache-Control": "no-cache"
                }
              }
            })
            .toPromise()
        } catch (cacheError) {
          console.warn("Cache invalidation failed:", cacheError) // eslint-disable-line no-console
        }

        showToast("Section deleted successfully", "success")
      } else if (result.data?.deleteBuilderSection?.errors?.length > 0) {
        showToast(result.data.deleteBuilderSection.errors[0], "error")
        throw new Error(result.data.deleteBuilderSection.errors[0])
      }
    } catch (error) {
      showToast("Failed to delete section", "error")
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const handleMoveSection = async (sectionId, directionOrOldIndex, newIndex) => {
    if (!currentPage) return

    try {
      setIsLoading(true)

      // Handle both old API (direction) and new API (oldIndex, newIndex)
      let direction = directionOrOldIndex
      if (typeof directionOrOldIndex === "number" && typeof newIndex === "number") {
        // New drag and drop API: perform multiple single-step moves until we reach target index
        let oldIndex = directionOrOldIndex
        if (oldIndex === newIndex) return
        direction = oldIndex < newIndex ? "down" : "up"
        const steps = Math.abs(newIndex - oldIndex)

        for (let i = 0; i < steps; i += 1) {
          const stepResult = await moveSection({
            pageSlug: currentPage.slug,
            sectionId,
            direction
          })
          if (!stepResult.data?.moveBuilderSection?.success) {
            const err = stepResult.data?.moveBuilderSection?.errors?.[0] || "Unknown error"
            showToast(err, "error")
            throw new Error(err)
          }

          // Apply one-step local swap to keep UI in sync with server
          setSections((prevSections) => {
            const sectionIndex = prevSections.findIndex((s) => s.id === sectionId)
            if (sectionIndex === -1) return prevSections
            const newSections = [...prevSections]
            const section = newSections[sectionIndex]
            if (direction === "up" && sectionIndex > 0) {
              const prevSection = newSections[sectionIndex - 1]
              newSections[sectionIndex - 1] = { ...section, order: prevSection.order }
              newSections[sectionIndex] = { ...prevSection, order: section.order }
            } else if (direction === "down" && sectionIndex < newSections.length - 1) {
              const nextSection = newSections[sectionIndex + 1]
              newSections[sectionIndex + 1] = { ...section, order: nextSection.order }
              newSections[sectionIndex] = { ...nextSection, order: section.order }
            }
            return newSections
          })
        }

        await refetchPages()
        showToast("Section moved successfully", "success")
      } else {
        // Legacy up/down API
        const result = await moveSection({
          pageSlug: currentPage.slug,
          sectionId,
          direction
        })
        if (result.data?.moveBuilderSection?.success) {
          setSections((prevSections) => {
            const sectionIndex = prevSections.findIndex((s) => s.id === sectionId)
            if (sectionIndex === -1) return prevSections
            const newSections = [...prevSections]
            const section = newSections[sectionIndex]
            if (direction === "up" && sectionIndex > 0) {
              const prevSection = newSections[sectionIndex - 1]
              newSections[sectionIndex - 1] = { ...section, order: prevSection.order }
              newSections[sectionIndex] = { ...prevSection, order: section.order }
            } else if (direction === "down" && sectionIndex < newSections.length - 1) {
              const nextSection = newSections[sectionIndex + 1]
              newSections[sectionIndex + 1] = { ...section, order: nextSection.order }
              newSections[sectionIndex] = { ...nextSection, order: section.order }
            }
            return newSections
          })
          await refetchPages()
          showToast("Section moved successfully", "success")
        } else if (result.data?.moveBuilderSection?.errors?.length > 0) {
          showToast(result.data.moveBuilderSection.errors[0], "error")
          throw new Error(result.data.moveBuilderSection.errors[0])
        }
      }
    } catch (error) {
      showToast("Failed to move section", "error")
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Theme management functions
  const handleUpdateTheme = async (themeData, options = {}) => {
    try {
      if (options.temporary) {
        // For temporary preview, update the preview state without affecting the main theme
        setPreviewTheme((prevPreviewTheme) => {
          const baseTheme = prevPreviewTheme || theme
          let updatedTheme = { ...baseTheme }

          // Handle colors
          if (themeData.colors) {
            updatedTheme.colors = {
              ...baseTheme?.colors,
              ...themeData.colors
            }

            // Always regenerate bgMedium and bgExtraLight when primary color changes
            const primaryColor = updatedTheme.colors.primary
            if (primaryColor) {
              const generatedPalette = generateColorPalette(primaryColor)
              // Always use the generated colors based on the current primary
              updatedTheme.colors.bgMedium = generatedPalette.bgMedium
              updatedTheme.colors.bgExtraLight = generatedPalette.bgExtraLight
            }
          }

          // Handle fonts
          if (themeData.fonts) {
            updatedTheme.fonts = {
              ...baseTheme?.fonts,
              ...themeData.fonts
            }
          }

          // Handle button style
          if (themeData.buttonStyle !== undefined) {
            updatedTheme.buttonStyle = themeData.buttonStyle
          }

          // Handle border radius
          if (themeData.borderRadius !== undefined) {
            updatedTheme.borderRadius = themeData.borderRadius
          }

          // Handle shape
          if (themeData.shape) {
            updatedTheme.shape = {
              ...baseTheme?.shape,
              ...themeData.shape
            }
          }

          // Handle header
          if (themeData.header) {
            updatedTheme.header = {
              ...baseTheme?.header,
              ...themeData.header
            }
          }

          // Handle footer
          if (themeData.footer) {
            updatedTheme.footer = {
              ...baseTheme?.footer,
              ...themeData.footer
            }
          }

          // If passing a full theme object and no specific properties were handled
          if (
            !themeData.colors &&
            !themeData.fonts &&
            themeData.buttonStyle === undefined &&
            themeData.borderRadius === undefined &&
            !themeData.shape &&
            !themeData.header &&
            !themeData.footer &&
            Object.keys(themeData).length > 0
          ) {
            updatedTheme = { ...baseTheme, ...themeData }
          }

          return updatedTheme
        })
        return themeData
      } else {
        // For permanent changes, save to database and clear preview
        setIsLoading(true)

        // Extract the data for the mutation
        const mutationData = {}
        if (themeData.colors) mutationData.colors = themeData.colors
        if (themeData.fonts) mutationData.fonts = themeData.fonts
        if (themeData.buttonStyle !== undefined) mutationData.buttonStyle = themeData.buttonStyle
        if (themeData.borderRadius !== undefined) mutationData.borderRadius = themeData.borderRadius
        if (themeData.shadowStyle !== undefined) mutationData.shadowStyle = themeData.shadowStyle
        if (themeData.spacingStyle !== undefined) mutationData.spacingStyle = themeData.spacingStyle
        if (themeData.header) mutationData.header = themeData.header
        if (themeData.footer) mutationData.footer = themeData.footer

        const result = await updateTheme(mutationData)
        if (result.data?.updateBuilderTheme?.theme) {
          const newTheme = transformThemeData(result.data.updateBuilderTheme.theme)
          setTheme(newTheme)
          setPreviewTheme(null) // Clear preview after saving
          showToast("Theme updated successfully", "success")
          return result.data.updateBuilderTheme.theme
        } else if (result.data?.updateBuilderTheme?.errors?.length > 0) {
          showToast(result.data.updateBuilderTheme.errors[0], "error")
          throw new Error(result.data.updateBuilderTheme.errors[0])
        }
      }
    } catch (error) {
      if (!options.temporary) {
        showToast("Failed to update theme", "error")
      }
      throw error
    } finally {
      if (!options.temporary) {
        setIsLoading(false)
      }
    }
  }

  const handleApplyPresetTheme = async (builderThemeId) => {
    try {
      setIsLoading(true)
      const result = await applyPresetTheme({ builderThemeId })
      if (result.data?.applyBuilderPresetTheme?.theme) {
        setTheme(transformThemeData(result.data.applyBuilderPresetTheme.theme))
        setPreviewTheme(null) // Clear preview when applying preset
        showToast("Preset theme applied successfully", "success")
        return result.data.applyBuilderPresetTheme.theme
      } else if (result.data?.applyBuilderPresetTheme?.errors?.length > 0) {
        showToast(result.data.applyBuilderPresetTheme.errors[0], "error")
        throw new Error(result.data.applyBuilderPresetTheme.errors[0])
      }
    } catch (error) {
      showToast("Failed to apply preset theme", "error")
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Function to clear preview and revert to stored theme
  const clearPreview = () => {
    setPreviewTheme(null)
    setPreviewSections(null)
  }

  // Function to update section preview temporarily
  const handleUpdateSectionPreview = (sectionId, sectionData) => {
    setPreviewSections(() => {
      const updatedSections = sections.map((section) => {
        if (section.id === sectionId) {
          return { ...section, ...sectionData }
        }
        return section
      })
      return updatedSections
    })
  }

  const value = {
    // State
    practice,
    setPractice,
    pages,
    setPages,
    currentPage,
    setCurrentPage,
    sections,
    setSections,
    theme,
    setTheme,
    previewTheme,
    previewSections,
    clearPreview,
    presetThemes,
    availableLayouts,
    selectedSection,
    setSelectedSection,
    highlightedField,
    setHighlightedField,
    isPreviewing,
    setIsPreviewing,
    openField,
    setOpenField,
    showAddSectionScreen,
    setShowAddSectionScreen,
    showEditSectionScreen,
    setShowEditSectionScreen,
    editingSection,
    setEditingSection,
    isLoading: pageLoading || themeLoading || isLoading || fetching,

    // Functions
    selectPage,
    handleCreatePage,
    handleUpdatePage,
    handleDeletePage,
    handleCreateSection,
    handleCreateMultipleSections,
    handleUpdateSection,
    handleDeleteSection,
    handleMoveSection,
    handleUpdateTheme,
    handleUpdateSectionPreview,
    handleApplyPresetTheme,
    activeAccordionSection,
    setActiveAccordionSection,
    expandedPages,
    setExpandedPages
  }

  return <WebsiteBuilderV2Context.Provider value={value}>{children}</WebsiteBuilderV2Context.Provider>
}

export const useWebsiteBuilderV2 = () => {
  const context = useContext(WebsiteBuilderV2Context)
  if (!context) {
    throw new Error("useWebsiteBuilderV2 must be used within a WebsiteBuilderV2Provider")
  }
  return context
}
